import { GRID_DOMAINS, yearLabel, type GridDomainKey, type GridWindow, type GridYear } from '../../../components/flashlightGrid';

export type AssignmentRowIn = { assignmentId: string; name: string; date: string; subDomain: string; score: number };
export type DomainAssignmentsIn = { domain: GridDomainKey; assignments: AssignmentRowIn[] };
export type AssignmentsYearIn = { schoolYear: number; assignments: DomainAssignmentsIn[] };

export type AssignmentPoint = { assignmentId: string; name: string; date: string; average: number; x: number };
export type BenchmarkMark = { value: number; x: number; label: string };
export type DomainChart = { domain: GridDomainKey; label: string; points: AssignmentPoint[]; newest: AssignmentPoint | null; benchmark: BenchmarkMark | null; line: string };
export type MonthCount = { label: string; text: string };
export type AssignmentsModel =
  | { kind: 'empty'; text: string }
  | { kind: 'charts'; months: string[]; counts: MonthCount[]; domains: DomainChart[]; meta: string; key: string };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SEPTEMBER = 2;
const BENCHMARK_X_IN_MONTH = 0.4;
const WINDOW_ORDER: GridWindow[] = ['boy', 'moy', 'eoy'];

function schoolMonthIndex(date: Date): number {
  return (date.getUTCMonth() + 6) % 12;
}

function calendarMonth(schoolYearEnd: number, index: number): { month: number; year: number } {
  const month = (index + 6) % 12;
  return { month, year: month >= 6 ? schoolYearEnd - 1 : schoolYearEnd };
}

function daysIn(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function pointsOf(rows: AssignmentRowIn[], start: number): AssignmentPoint[] {
  const groups = new Map<string, { name: string; date: string; scores: number[] }>();
  for (const row of rows) {
    const group = groups.get(row.assignmentId) ?? { name: row.name, date: row.date, scores: [] };
    group.scores.push(row.score);
    if (row.date < group.date) group.date = row.date;
    groups.set(row.assignmentId, group);
  }
  return [...groups.entries()]
    .map(([assignmentId, group]) => {
      const when = new Date(group.date);
      const x = schoolMonthIndex(when) - start + (when.getUTCDate() - 1) / daysIn(when.getUTCFullYear(), when.getUTCMonth());
      return { assignmentId, name: group.name, date: group.date, average: group.scores.reduce((a, b) => a + b, 0) / group.scores.length, x };
    })
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.assignmentId.localeCompare(b.assignmentId)));
}

function newestBenchmark(benchmarks: GridYear | undefined, domain: GridDomainKey): { value: number; monthIndex: number; label: string } | null {
  const scored = benchmarks?.benchmarks.find((d) => d.domain === domain);
  if (!scored) return null;
  const candidates = WINDOW_ORDER.flatMap((window) => {
    const value = scored.average[window];
    const month = scored.windows?.[window] ?? benchmarks?.windows?.[window];
    return value == null || !month ? [] : [{ value, month }];
  }).sort((a, b) => (a.month < b.month ? 1 : a.month > b.month ? -1 : 0));
  const newest = candidates[0];
  if (!newest) return null;
  const mm = Number(newest.month.split('-')[1]) - 1;
  return { value: newest.value, monthIndex: (mm + 6) % 12, label: `${FULL_MONTHS[mm]} benchmark` };
}

function benchmarkOf(benchmarks: GridYear | undefined, domain: GridDomainKey, start: number): BenchmarkMark | null {
  const newest = newestBenchmark(benchmarks, domain);
  return newest ? { value: newest.value, x: newest.monthIndex - start + BENCHMARK_X_IN_MONTH, label: newest.label } : null;
}

export function assignmentsModelOf(years: AssignmentsYearIn[], thisYear: number, benchmarks: GridYear | undefined, now: Date): AssignmentsModel {
  const year = years.find((y) => y.schoolYear === thisYear);
  const rows = (year?.assignments.flatMap((d) => d.assignments.map((row) => ({ ...row, domain: d.domain }))) ?? []).filter((row) => !Number.isNaN(new Date(row.date).getTime()));
  if (rows.length === 0) return { kind: 'empty', text: `No assignments scored yet in ${yearLabel(thisYear)}.` };
  const current = schoolMonthIndex(now);
  const yearBenchmarks = benchmarks?.schoolYear === thisYear ? benchmarks : undefined;
  const benchmarkMonths = GRID_DOMAINS.map(({ key }) => newestBenchmark(yearBenchmarks, key)?.monthIndex).filter((m): m is number => m != null);
  const earliest = Math.min(...rows.map((row) => schoolMonthIndex(new Date(row.date))), ...benchmarkMonths);
  const start = Math.min(SEPTEMBER, earliest, current);
  const end = Math.max(current, start);
  const months = Array.from({ length: end - start + 1 }, (_, i) => MONTHS[calendarMonth(thisYear, start + i).month]!);
  const distinct = new Map<string, string>();
  for (const row of rows) {
    const date = distinct.get(row.assignmentId);
    if (!date || row.date < date) distinct.set(row.assignmentId, row.date);
  }
  const counts = months.map((label, i) => {
    const index = start + i;
    const count = [...distinct.values()].filter((date) => schoolMonthIndex(new Date(date)) === index).length;
    return { label, text: index === current ? `${count} so far` : `${count} scored` };
  });
  const domains: DomainChart[] = GRID_DOMAINS.flatMap(({ key, label }) => {
    const points = pointsOf(rows.filter((row) => row.domain === key), start);
    if (points.length === 0) return [];
    const newest = points[points.length - 1]!;
    const benchmark = benchmarkOf(yearBenchmarks, key, start);
    const line = `Newest assignment ${newest.average.toFixed(1)}${benchmark ? ` · ${benchmark.label} ${benchmark.value.toFixed(1)}` : ''}`;
    return [{ domain: key, label, points, newest, benchmark, line }];
  });
  const newestDate = [...distinct.values()].sort().pop()!;
  const when = new Date(newestDate);
  const marks = domains.map((d) => d.benchmark).filter((b): b is BenchmarkMark => b != null);
  const key = marks.length ? `Diamond: ${marks.sort((a, b) => b.x - a.x)[0]!.label} · dots: assignments, domain average` : 'Dots: assignments, domain average';
  return {
    kind: 'charts',
    months,
    counts,
    domains,
    meta: `${yearLabel(thisYear)} · ${distinct.size} scored · newest ${MONTHS[when.getUTCMonth()]} ${when.getUTCDate()}`,
    key,
  };
}
